import {
  Bar,
  Button,
  Dialog,
  FileUploader,
  FlexBox,
  FlexBoxDirection,
  FlexBoxJustifyContent,
  Label,
  ObjectStatus,
  Popover,
  Table,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  Toast
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/display';
import '@ui5/webcomponents-icons/dist/upload';
import '@ui5/webcomponents-icons/dist/connected';
import { QtImportStatus, useGetAllQueryTrackerImportsQuery } from 'generated/graphql';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './ImportDialog.jss';
import { createPortal } from 'react-dom';
import { getFormattedDateString } from 'hooks/useGetDate';
import { useFilesForImport } from 'hooks/useFilesForImport';
import dayjs from 'dayjs';
import { QTProjectImportDialog } from '../QTProjectImportDialog/QTProjectImportDialog';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';

type ImportDialogProps = {
  isDialogOpen: boolean;
  setIsDialogOpen: (isOpen: boolean) => void;
};

export const ImportDialog: React.FC<ImportDialogProps> = (props) => {
  const { isDialogOpen, setIsDialogOpen } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const [{ fetching, data: qtImportData }, reexecuteQuery] = useGetAllQueryTrackerImportsQuery();
  const [popoverIsOpen, setPopoverIsOpen] = useState<string>('');
  const toastRef = useRef<any>();
  const refetchData = () => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  };
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const { handleFileImport, toastMessage, toastOpen, setToastOpen } = useFilesForImport({
    refetchData,
    setIsUploadingFile
  });
  const [isQTProjectImportDialogOpen, setIsQTProjectImportDialogOpen] = useState<boolean>(false);

  const statusValueState: Map<QtImportStatus, ValueState> = new Map([
    [QtImportStatus.ERROR, ValueState.Negative],
    [QtImportStatus.PROCESSING, ValueState.Information],
    [QtImportStatus.SUCCESS, ValueState.Positive],
    [QtImportStatus.WARNING, ValueState.Critical]
  ]);

  return createPortal(
    <>
      <Dialog
        open={isDialogOpen}
        footer={
          <Bar
            design="Footer"
            endContent={
              <Button onClick={() => setIsDialogOpen(false)}>
                {t('manage-queries-import-query-dialog-button-close')}
              </Button>
            }
          />
        }
        headerText={t('manage-queries-import-query-dialog-header')}
        onClose={() => setIsDialogOpen(false)}
      >
        <FlexBox
          direction={FlexBoxDirection.Row}
          justifyContent={FlexBoxJustifyContent.SpaceBetween}
        >
          <div className={classes.importSelectDiv}>
            <Label wrappingType="None">
              {t('manage-queries-import-query-dialog-file-selection-label')}
            </Label>
            <FileUploader
              hideInput
              multiple={false}
              accept=".json"
              onChange={handleFileImport}
              data-testid={'file-uploader'}
            >
              <Button
                data-testid={'import-dialog-upload-button'}
                icon="upload"
                tooltip={t('manage-queries-import-query-dialog-button-import-tooltip')}
                className={classes.importButton}
              />
            </FileUploader>
            <Text className={classes.orText}>
              {t('manage-queries-import-query-dialog-button-import-or-text')}
            </Text>
            <Button
              data-testid={'qt-import-dialog-button'}
              icon="connected"
              design={ButtonDesign.Default}
              onClick={() => setIsQTProjectImportDialogOpen(true)}
            >
              {t('manage-queries-import-query-dialog-button-import-abap')}
            </Button>
          </div>
          <Button
            data-testid={'import-dialog-refresh-button'}
            icon="refresh"
            design={ButtonDesign.Transparent}
            tooltip={t('manage-queries-import-query-dialog-button-refresh-tooltip')}
            onClick={() => reexecuteQuery({ requestPolicy: 'network-only' })}
          />
        </FlexBox>
        <Table
          className={classes.importTable}
          loading={fetching || isUploadingFile}
          headerRow={
            <TableHeaderRow>
              <TableHeaderCell>
                <Label wrappingType="None">
                  {t('manage-queries-import-query-dialog-column-status')}
                </Label>
              </TableHeaderCell>
              <TableHeaderCell>
                <Label wrappingType="None">
                  {t('manage-queries-import-query-dialog-column-user')}
                </Label>
              </TableHeaderCell>
              <TableHeaderCell>
                <Label wrappingType="None">
                  {t('manage-queries-import-query-dialog-column-file-name')}
                </Label>
              </TableHeaderCell>
              <TableHeaderCell>
                <Label wrappingType="None">
                  {t('manage-queries-import-query-dialog-column-started')}
                </Label>
              </TableHeaderCell>
              <TableHeaderCell>
                <Label wrappingType="None">
                  {t('manage-queries-import-query-dialog-column-finished')}
                </Label>
              </TableHeaderCell>
              <TableHeaderCell>
                <Label wrappingType="None">
                  {t('manage-queries-import-query-dialog-column-details')}
                </Label>
              </TableHeaderCell>
            </TableHeaderRow>
          }
        >
          {qtImportData?.qtImports?.map((qtImport) => (
            <TableRow key={qtImport.id} data-testid={`imports-table-row-${qtImport.id}`}>
              <TableCell>
                <ObjectStatus
                  data-testid={`imports-table-status-row-${qtImport.id}`}
                  showDefaultIcon
                  state={statusValueState.get(qtImport.status)}
                >
                  {t(`manage-queries-import-query-dialog-status-${qtImport.status.toLowerCase()}`)}
                </ObjectStatus>
              </TableCell>
              <TableCell>{qtImport.user.displayName}</TableCell>
              <TableCell>{qtImport.fileName}</TableCell>
              <TableCell>{getFormattedDateString(qtImport.createdAt, true)}</TableCell>
              <TableCell>
                {dayjs(qtImport.finishedAt).isValid()
                  ? getFormattedDateString(qtImport.finishedAt, true)
                  : ''}
              </TableCell>
              {[QtImportStatus.ERROR, QtImportStatus.WARNING].includes(qtImport.status) && (
                <TableCell>
                  <Button
                    id={`${qtImport.id}-details-button`}
                    data-testid={`${qtImport.id}-details-button`}
                    design={ButtonDesign.Transparent}
                    icon="display"
                    tooltip={t('manage-queries-import-query-dialog-button-error-details-tooltip')}
                    onClick={() => setPopoverIsOpen(qtImport.id)}
                  ></Button>
                  <Popover
                    data-testid={`${qtImport.id}-details-popover`}
                    headerText={t(
                      'manage-queries-import-query-dialog-error-details-popover-header'
                    )}
                    opener={`${qtImport.id}-details-button`}
                    open={popoverIsOpen === qtImport.id}
                    onClose={() => {
                      setPopoverIsOpen('');
                    }}
                  >
                    <Text>{qtImport.details}</Text>
                  </Popover>
                </TableCell>
              )}
            </TableRow>
          ))}
        </Table>
      </Dialog>
      <Toast ref={toastRef} open={toastOpen} onClose={() => setToastOpen(false)}>
        {toastMessage}
      </Toast>
      <QTProjectImportDialog
        isDialogOpen={isQTProjectImportDialogOpen}
        setIsDialogOpen={setIsQTProjectImportDialogOpen}
      />
    </>,
    document.body
  );
};
